import { useEffect, useState } from 'react';

import { PERSONAS, PersonaId } from '../../personas';
import { TOP_PERSONAS_SHOWN } from '../constants';
import {
  IconCaterer,
  IconConsultant,
  IconCreator,
  IconDaycare,
  IconDesigner,
  IconDeveloper,
  IconElectrician,
  IconFitness,
  IconGardener,
  IconHairStylist,
  IconHealthcare,
  IconMaid,
  IconMechanic,
  IconMore,
  IconMover,
  IconMusic,
  IconPainter,
  IconPhotographer,
  IconPlumber,
  IconTutor,
  IconVideographer,
} from './icons';
import { GlobalScrollLock } from 'components/GlobalScrollLock';
import * as styled from '../styles/persona-gate';

const PERSONA_ICONS: Record<PersonaId, React.ComponentType> = {
  content_creator: IconCreator,
  cleaner: IconMaid,
  electrician: IconElectrician,
  painter: IconPainter,
  hair_stylist: IconHairStylist,
  daycare: IconDaycare,
  healthcare: IconHealthcare,
  plumber: IconPlumber,
  mechanic: IconMechanic,
  mover: IconMover,
  gardener: IconGardener,
  tutor: IconTutor,
  caterer: IconCaterer,
  trainer: IconFitness,
  photographer: IconPhotographer,
  designer: IconDesigner,
  developer: IconDeveloper,
  videographer: IconVideographer,
  dj: IconMusic,
  legal: IconConsultant,
  other: IconMore,
};

type Props = {
  onSelect: (id: PersonaId) => void;
  onClose: () => void;
};

export const PersonaGate = ({ onSelect, onClose }: Props) => {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const trades = PERSONAS.filter((persona) => persona.id !== 'other');
  const otherPersona = PERSONAS.find((persona) => persona.id === 'other')!;

  const filter = query.trim().toLowerCase();
  const tokens = filter.split(/\s+/).filter(Boolean);
  const shown = filter
    ? trades.filter((persona) => {
        const label = persona.label.toLowerCase();
        return tokens.some((token) => label.includes(token));
      })
    : showAll
      ? trades
      : trades.slice(0, TOP_PERSONAS_SHOWN);
  const hiddenCount = trades.length - TOP_PERSONAS_SHOWN;

  return (
    <styled.Overlay onClick={onClose}>
      <GlobalScrollLock />
      <styled.Card onClick={(event) => event.stopPropagation()}>
        <styled.CloseButton onClick={onClose} />
        <styled.Heading>What kind of pro are you?</styled.Heading>
        <styled.Subheading>
          Pick the closest fit to get started.
        </styled.Subheading>

        <styled.FilterInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Type your trade — e.g. hair stylist or trainer"
        />

        <styled.Grid>
          {shown.map((persona) => {
            const Icon = PERSONA_ICONS[persona.id];
            return (
              <styled.PersonaCard
                key={persona.id}
                onClick={() => onSelect(persona.id)}
                type="button"
              >
                <Icon />
                <styled.PersonaLabel>{persona.label}</styled.PersonaLabel>
              </styled.PersonaCard>
            );
          })}
        </styled.Grid>

        {filter !== '' && shown.length === 0 && (
          <styled.EmptyHint>
            Can&apos;t see yours? Pick &ldquo;{otherPersona.label}&rdquo; below
            — you&apos;re still covered.
          </styled.EmptyHint>
        )}

        {filter === '' && !showAll && (
          <styled.ShowAllButton type="button" onClick={() => setShowAll(true)}>
            Show all trades (+{hiddenCount})
          </styled.ShowAllButton>
        )}

        <styled.SkipRow>
          <styled.SkipButton
            onClick={() => onSelect(otherPersona.id)}
            type="button"
          >
            {otherPersona.label} / skip
          </styled.SkipButton>
        </styled.SkipRow>
      </styled.Card>
    </styled.Overlay>
  );
};
